/**
 * RecipientSelector — таблица пациентов с чекбоксами для ручной рассылки
 * Дата: 17.03.2026 — добавлена антиспам-подсказка по последней manual-отправке
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  manualBroadcastsApi,
  type BroadcastRecipientPreview,
  type LatestPatientManualHistory,
  type PreviewManualBroadcastRecipientRequest,
  type SegmentPatient,
} from '@/api/manualBroadcasts';
import { getChannelLabel } from '@/config/channels';
import { Search, Users, CheckSquare, Square, Loader2, Eye } from 'lucide-react';
import { PageSizeSelect, Pagination, Modal, LoadingState } from '@/components/ui';
import { ManualBroadcastTestSection } from '@/components/features/manual-broadcasts/ManualBroadcastTestSection';
import { ResolvedTemplatePreview } from '@/components/features/templates/ResolvedTemplatePreview';
import { isWabaBackedTemplate } from '@/features/templates/templateTaxonomy';

interface RecipientSelectorProps {
  segmentType: 'regular' | 'dynamic' | 'lk';
  segmentId: number;
  tenantId?: string;
  selectedPhones: Set<string>;
  onSelectionChange: (phones: Set<string>) => void;
  previewRequestFactory?: (patient: SegmentPatient) => PreviewManualBroadcastRecipientRequest | null;
}

function normalizePhoneLookup(phone?: string): string {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 11 && digits.startsWith('8')) return `7${digits.slice(1)}`;
  if (digits.length === 11 && digits.startsWith('7')) return digits;
  if (digits.length === 10) return `7${digits}`;
  if (digits.length === 12 && digits.startsWith('87')) return digits.slice(1);
  return digits;
}

function formatLastSendAt(value?: string): string {
  if (!value) return 'Дата неизвестна';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function RecipientSelector({
  segmentType,
  segmentId,
  tenantId,
  selectedPhones,
  onSelectionChange,
  previewRequestFactory,
}: RecipientSelectorProps) {
  const [patients, setPatients] = useState<SegmentPatient[]>([]);
  const [latestHistoryByPhone, setLatestHistoryByPhone] = useState<Record<string, LatestPatientManualHistory>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [previewRecipient, setPreviewRecipient] = useState<BroadcastRecipientPreview | null>(null);
  const [previewRequest, setPreviewRequest] = useState<PreviewManualBroadcastRecipientRequest | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (!segmentId) return;
    let cancelled = false;
    const loadPatients = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await manualBroadcastsApi.loadAllSegmentPatients(segmentType, segmentId, tenantId);
        if (cancelled) return;

        const lookupPhones = Array.from(
          new Set(
            list
              .map((patient) => normalizePhoneLookup(patient.phone))
              .filter(Boolean),
          ),
        );

        let latestHistory: Record<string, LatestPatientManualHistory> = {};
        if (lookupPhones.length > 0) {
          try {
            latestHistory = await manualBroadcastsApi.getPatientsLatestHistory(lookupPhones, tenantId);
          } catch {
            latestHistory = {};
          }
        }
        if (cancelled) return;

        setPatients(list);
        setLatestHistoryByPhone(latestHistory);
        if (selectedPhones.size === 0) {
          onSelectionChange(
            new Set(
              list
                .map((patient) => patient.phone)
                .filter((phone) => normalizePhoneLookup(phone)),
            ),
          );
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Не удалось загрузить пациентов');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPatients();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentType, segmentId, tenantId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return patients;
    const q = search.toLowerCase();
    return patients.filter(
      (p) => p.name?.toLowerCase().includes(q) || p.phone?.includes(q)
    );
  }, [patients, search]);

  useEffect(() => {
    setPage(0);
  }, [search, pageSize, segmentType, segmentId, tenantId]);

  const pageOffset = page * pageSize;
  const pageItems = useMemo(
    () => filtered.slice(pageOffset, pageOffset + pageSize),
    [filtered, pageOffset, pageSize],
  );

  const allFilteredSelected = useMemo(
    () => {
      const selectable = filtered.filter((patient) => normalizePhoneLookup(patient.phone));
      return selectable.length > 0 && selectable.every((patient) => selectedPhones.has(patient.phone));
    },
    [filtered, selectedPhones]
  );

  const toggleAll = useCallback(() => {
    const next = new Set(selectedPhones);
    if (allFilteredSelected) {
      for (const patient of filtered) {
        if (!normalizePhoneLookup(patient.phone)) continue;
        next.delete(patient.phone);
      }
    } else {
      for (const patient of filtered) {
        if (!normalizePhoneLookup(patient.phone)) continue;
        next.add(patient.phone);
      }
    }
    onSelectionChange(next);
  }, [allFilteredSelected, filtered, selectedPhones, onSelectionChange]);

  const toggleOne = useCallback(
    (phone: string) => {
      if (!normalizePhoneLookup(phone)) return;
      const next = new Set(selectedPhones);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      onSelectionChange(next);
    },
    [selectedPhones, onSelectionChange]
  );

  // 17.03.2026 - Единый on-demand preview по конкретному пациенту нужен и в wizard create,
  // и в странице draft recipients. Данные рендера считаем на backend, чтобы WABA/text
  // preview использовал ту же серверную логику подстановки переменных и канальной адаптации.
  const handleOpenPreview = useCallback(async (patient: SegmentPatient) => {
    if (!previewRequestFactory) return;
    const request = previewRequestFactory(patient);
    if (!request) return;
    try {
      setPreviewLoading(true);
      setPreviewError('');
      setPreviewRequest(request);
      const data = await manualBroadcastsApi.previewRecipient(request, tenantId);
      setPreviewRecipient(data);
    } catch (e: any) {
      setPreviewRecipient(null);
      setPreviewRequest(null);
      setPreviewError(e?.response?.data?.error || 'Не удалось построить предпросмотр');
    } finally {
      setPreviewLoading(false);
    }
  }, [previewRequestFactory, tenantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-neutral-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
        Загрузка пациентов...
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 py-4">{error}</div>;
  }

  if (patients.length === 0) {
    return <div className="text-neutral-500 py-4">Нет пациентов в сегменте</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по имени или телефону..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-600 whitespace-nowrap">
          <Users className="w-4 h-4" />
          <span>{selectedPhones.size} / {patients.length}</span>
        </div>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="text-xs text-neutral-500">
          Найдено {filtered.length} из {patients.length}
        </div>
        <PageSizeSelect value={pageSize} onChange={setPageSize} />
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b">
            <tr>
              <th className="w-10 px-3 py-2 text-left">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-neutral-600 hover:text-primary-600"
                  title={allFilteredSelected ? 'Снять все' : 'Выбрать все'}
                >
                  {allFilteredSelected ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-neutral-700">Имя</th>
              <th className="px-3 py-2 text-left font-medium text-neutral-700">Телефон</th>
              <th className="w-14 px-3 py-2 text-left font-medium text-neutral-700">Глазик</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((p, index) => {
              const checked = selectedPhones.has(p.phone);
              const lookupPhone = normalizePhoneLookup(p.phone);
              const hasPhone = Boolean(lookupPhone);
              const latestHistory = lookupPhone ? latestHistoryByPhone[lookupPhone] : undefined;
              const hasPreview = Boolean(previewRequestFactory && hasPhone);
              return (
                <tr
                  key={`${p.phone || 'no-phone'}:${index}`}
                  onClick={() => toggleOne(p.phone)}
                  className={`border-b last:border-0 transition-colors ${
                    !hasPhone
                      ? 'bg-neutral-50 text-neutral-400 cursor-not-allowed'
                      : checked
                      ? 'bg-primary-50 hover:bg-primary-100'
                      : 'hover:bg-neutral-50 cursor-pointer'
                  }`}
                >
                  <td className="px-3 py-1.5">
                    {!hasPhone ? (
                      <Square className="w-4 h-4 text-neutral-300" />
                    ) : checked ? (
                      <CheckSquare className="w-4 h-4 text-primary-600" />
                    ) : (
                      <Square className="w-4 h-4 text-neutral-400" />
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className={hasPhone ? 'text-neutral-900' : 'text-neutral-500'}>{p.name || '—'}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      {!hasPhone
                        ? 'Нет телефона для ручной отправки'
                        : latestHistory
                          ? `Последняя ручная: ${latestHistory.template_name} • ${getChannelLabel(latestHistory.channel)} • ${formatLastSendAt(latestHistory.sent_at || latestHistory.created_at)}`
                          : 'Ручных отправок не было'}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-neutral-600 font-mono text-xs">{p.phone || '—'}</td>
                  <td className="px-3 py-1.5">
                    {hasPreview ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleOpenPreview(p);
                        }}
                        className="inline-flex items-center rounded border border-neutral-200 p-1.5 text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                        title="Открыть предпросмотр сообщения"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    ) : (
                      <span className="text-neutral-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        offset={pageOffset}
        limit={pageSize}
        total={filtered.length}
        onOffsetChange={(nextOffset) => setPage(Math.floor(nextOffset / pageSize))}
        showPageNumbers
      />

      <Modal
        isOpen={previewLoading || Boolean(previewRecipient) || Boolean(previewError)}
        onClose={() => {
          setPreviewRecipient(null);
          setPreviewRequest(null);
          setPreviewError('');
          setPreviewLoading(false);
        }}
        title={previewRecipient?.patient_name || 'Предпросмотр сообщения'}
        size="lg"
      >
        {previewLoading ? (
          <LoadingState text="Загрузка предпросмотра..." size="sm" inline />
        ) : previewError ? (
          <div className="py-4 text-sm text-red-600">{previewError}</div>
        ) : previewRecipient ? (
          <div className="space-y-4">
            <ResolvedTemplatePreview
              preview={previewRecipient.preview_text || ''}
              wabaPreview={previewRecipient.waba_preview}
              channel={previewRecipient.channel}
              isWabaTemplate={isWabaBackedTemplate({ template_type: previewRecipient.template_type })}
              testId="manual-broadcast-recipient-preview"
            />
            {previewRequest && (
              <ManualBroadcastTestSection
                request={previewRequest}
                tenantId={tenantId}
                currentChannel={previewRecipient.channel}
              />
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
